// Wrapper around network request, can be used on client and server

import { t } from 'i18next';

export type NetworkResponse = {
  body: any;
  status: number;
  header: Record<string, string>;
};

export type INetworkProvider = {
  get(url: string): Promise<any>;
  post(url: string, payload?: any): Promise<any>;
  put(url: string, payload?: any): Promise<any>;
  patch(url: string, payload?: any): Promise<any>;
  delete(url: string, payload?: any): Promise<any>;
};

export interface IFrontendNetworkService extends INetworkProvider {
  init(provider: INetworkProvider): void;
}

let currentProvider: INetworkProvider | null = null;

function handleError({ err, response }: { err: Error; response: NetworkResponse | null }) {
  const htmlError = handleHtmlError(err, response);
  if (htmlError) {
    throw htmlError;
  }
  throw err;
}

function handleHtmlError(err: Error, response: NetworkResponse | null = null) {
  if (response && response.header && response.header['content-type']) {
    const contentType = response.header['content-type'];
    if (contentType.includes('text/html')) {
      return handleHtmlErrorResponse(response.status);
    }
  }
  return null;
}

function handleHtmlErrorResponse(statusCode: number) {
  let errorMessage;
  if (statusCode && statusCode >= 400 && statusCode < 500) {
    errorMessage = t('common:errors.request-error');
  } else if (statusCode && statusCode >= 500 && statusCode < 600) {
    errorMessage = t('common:errors.something-went-wrong');
  } else {
    errorMessage = t('common:errors.unknown-error');
  }

  return new Error(errorMessage);
}

export const networkService: IFrontendNetworkService = {
  init(provider: INetworkProvider) {
    currentProvider = provider;
  },

  async get(url: string) {
    if (!currentProvider) {
      throw new Error('network provider not setup');
    }

    try {
      return await currentProvider.get(url);
    } catch (err: any) {
      handleError({ err, response: err?.response || null });
    }
  },

  async post(url: string, payload?: any) {
    if (!currentProvider) {
      throw new Error('network provider not setup');
    }

    try {
      return await currentProvider.post(url, payload);
    } catch (err: any) {
      handleError({ err, response: err?.response || null });
    }
  },

  async put(url: string, payload?: any) {
    if (!currentProvider) {
      throw new Error('network provider not setup');
    }

    try {
      return await currentProvider.put(url, payload);
    } catch (err: any) {
      handleError({ err, response: err?.response || null });
    }
  },

  async patch(url: string, payload?: any) {
    if (!currentProvider) {
      throw new Error('network provider not setup');
    }

    try {
      return await currentProvider.patch(url, payload);
    } catch (err: any) {
      handleError({ err, response: err?.response || null });
    }
  },

  async delete(url: string, payload?: any) {
    if (!currentProvider) {
      throw new Error('network provider not setup');
    }

    try {
      return await currentProvider.delete(url, payload);
    } catch (err: any) {
      handleError({ err, response: err?.response || null });
    }
  }
};
